// Editor boxes use the rotated pdf.js viewport; stored boxes use unrotated PDF coordinates.
export const PUBLIC_ANNOTATION_PREFIX = '[PUBLIC — redaction skipped]'
const NON_REDACTION_STROKE_COLORS = [
  [0, 153, 0],
  [0, 0, 204]
]
const COLOR_TOLERANCE = 3
export const MANUAL_ANNOTATION_TITLE = 'Manual redaction'

export const MIN_BOX_POINTS = 2

export function round2(value) {
  return Math.round(value * 100) / 100
}

export function pointsToPx(box, scale) {
  return { left: box.x * scale, top: box.y * scale, width: box.w * scale, height: box.h * scale }
}

export function pxToPoints(px, scale) {
  return { x: round2(px.left / scale), y: round2(px.top / scale), w: round2(px.width / scale), h: round2(px.height / scale) }
}

export function normalizeRect([x1, y1, x2, y2]) {
  const x = Math.min(x1, x2)
  const y = Math.min(y1, y2)
  return { x: round2(x), y: round2(y), w: round2(Math.abs(x2 - x1)), h: round2(Math.abs(y2 - y1)) }
}

export function clampBox(box, pageWidth, pageHeight) {
  const w = Math.min(Math.max(box.w, MIN_BOX_POINTS), pageWidth)
  const h = Math.min(Math.max(box.h, MIN_BOX_POINTS), pageHeight)
  const x = Math.min(Math.max(box.x, 0), pageWidth - w)
  const y = Math.min(Math.max(box.y, 0), pageHeight - h)
  return { ...box, x: round2(x), y: round2(y), w: round2(w), h: round2(h) }
}

function colorsMatch(actual, expected) {
  if (!actual || actual.length < 3) return false
  return expected.every((value, i) => Math.abs(actual[i] - value) <= COLOR_TOLERANCE)
}

export function annotationReason(annot) {
  return annot?.contentsObj?.str ?? annot?.contents ?? ''
}

export function annotationTitle(annot) {
  return annot?.titleObj?.str ?? annot?.title ?? ''
}

// Match the worker's redaction annotation filter.
export function isRedactionAnnotation(annot) {
  if (!annot || annot.subtype !== 'Square') return false
  if (annotationReason(annot).startsWith(PUBLIC_ANNOTATION_PREFIX)) return false
  const stroke = annot.color ? Array.from(annot.color) : null
  return !NON_REDACTION_STROKE_COLORS.some(debugColor => colorsMatch(stroke, debugColor))
}

export function annotationOrigin(annot) {
  return annotationTitle(annot) === MANUAL_ANNOTATION_TITLE ? 'manual' : 'ai'
}

let nextBoxId = 1
export function newBoxId() {
  nextBoxId += 1
  return `box-${Date.now().toString(36)}-${nextBoxId}`
}

export function annotationToBox(annot, viewport, pageIndex) {
  const rect = normalizeRect(viewport.convertToViewportRectangle(annot.rect))
  return {
    id: newBoxId(),
    page: pageIndex,
    ...rect,
    reason: annotationReason(annot),
    origin: annotationOrigin(annot)
  }
}

// Convert a displayed box back to the worker's unrotated coordinate space.
export function boxToUnrotatedRect(box, viewport) {
  if (typeof viewport?.convertToPdfPoint !== 'function' || !Array.isArray(viewport.viewBox)) {
    return { x: round2(box.x), y: round2(box.y), w: round2(box.w), h: round2(box.h) }
  }
  const [ax, ay] = viewport.convertToPdfPoint(box.x, box.y)
  const [bx, by] = viewport.convertToPdfPoint(box.x + box.w, box.y + box.h)
  const [viewLeft, , , viewTop] = viewport.viewBox
  return {
    x: round2(Math.min(ax, bx) - viewLeft),
    y: round2(viewTop - Math.max(ay, by)),
    w: round2(Math.abs(bx - ax)),
    h: round2(Math.abs(by - ay))
  }
}

export function boxesToPayload(boxes, pages = []) {
  return boxes.map(({ page, x, y, w, h, reason, origin }) => ({
    page,
    ...boxToUnrotatedRect({ x, y, w, h }, pages[page]?.getViewport?.({ scale: 1 })),
    reason: reason ?? '',
    origin: origin === 'manual' ? 'manual' : 'ai'
  }))
}

export function serializeBoxes(boxes) {
  return JSON.stringify(
    boxes
      .map(b => [b.page, round2(b.x), round2(b.y), round2(b.w), round2(b.h), b.reason ?? '', b.origin ?? 'ai'])
      .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
  )
}

// Count unique redaction labels embedded in an applied PDF.
export async function countAppliedRedactions(pages) {
  const labels = new Set()
  for (const page of pages) {
    const content = await page.getTextContent()
    const text = (content?.items ?? []).map(item => item.str ?? '').join(' ')
    for (const match of text.matchAll(/\bR-(\d{4,})\b/g)) labels.add(match[1])
  }
  return labels.size
}

// Approximate the text covered by a box for redact-all prefill.
export function textUnderBox(textContent, viewport, box, { maxLength = 200 } = {}) {
  const parts = []
  for (const item of textContent?.items ?? []) {
    const str = item.str ?? ''
    if (!str.trim() || !Array.isArray(item.transform) || !(item.width > 0)) continue
    const [, , , , originX, originY] = item.transform
    const rect = normalizeRect(viewport.convertToViewportRectangle([originX, originY, originX + item.width, originY + (item.height || 0)]))
    const centreY = rect.y + rect.h / 2
    if (centreY < box.y || centreY > box.y + box.h) continue
    const overlapLeft = Math.max(rect.x, box.x)
    const overlapRight = Math.min(rect.x + rect.w, box.x + box.w)
    if (overlapRight - overlapLeft <= 0) continue
    const start = Math.max(0, Math.floor(((overlapLeft - rect.x) / rect.w) * str.length))
    const end = Math.min(str.length, Math.ceil(((overlapRight - rect.x) / rect.w) * str.length))
    const slice = str.slice(start, end).trim()
    if (slice) parts.push(slice)
  }
  return parts.join(' ').replace(/\s+/g, ' ').trim().slice(0, maxLength)
}
